import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { Webinar, WebinarWhen } from '@proref/types';
import type { UpdateWebinarInput } from '@proref/schemas';
import {
  listWebinars,
  createWebinar,
  updateWebinar,
  reserveWebinar,
  cancelWebinarReservation,
  deleteWebinar,
  getWebinarAttendees,
} from '../services/webinars.service';

// BASE_KEY is the prefix every webinars query lives under; TanStack Query's default (non-`exact`)
// invalidation matches by prefix, so `invalidateQueries({ queryKey: BASE_KEY })` refetches BOTH the
// 'upcoming' and 'past' lists (and Home's, which share the same key) without naming them separately.
const BASE_KEY = ['webinars'] as const;
const keyFor = (when: WebinarWhen) => [...BASE_KEY, when] as const;
// The only key a reservation toggle ever needs to patch optimistically — reserving/cancelling a
// PAST webinar isn't offered in the UI, so `applyReservation` below never has a 'past' cache to fix.
const UPCOMING_KEY = keyFor('upcoming');

export function useWebinarsQuery(when: WebinarWhen = 'upcoming') {
  return useQuery({
    queryKey: keyFor(when),
    queryFn: () => listWebinars(when),
    staleTime: 30_000,
    // Show the previous filter's rows while the new key loads, instead of dropping to a skeleton.
    // This does NOT make data staler: staleTime and invalidation are untouched, so a mutation
    // elsewhere still refetches and replaces this immediately. It only fills the gap where a key
    // change has no cached entry yet — and `isPlaceholderData` lets the page mark it provisional.
    placeholderData: keepPreviousData,
  });
}

// The host's attendee roster for one webinar — fetched only while its detail modal is open AND the
// viewer can manage it (`enabled`), since the API 403s anyone else and there's no reason to ask.
export function useWebinarAttendeesQuery(webinarId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['webinars', webinarId, 'attendees'],
    queryFn: () => getWebinarAttendees(webinarId as string),
    enabled: Boolean(webinarId) && enabled,
    staleTime: 15_000,
  });
}

// Apply a reservation change to one cached webinar: flip is_reserved, nudge the count, and recompute
// seats_left (null stays null — an uncapped webinar has no seat count).
function applyReservation(w: Webinar, reserve: boolean): Webinar {
  const reservation_count = Math.max(0, w.reservation_count + (reserve ? 1 : -1));
  return {
    ...w,
    is_reserved: reserve,
    reservation_count,
    seats_left: w.capacity === null ? null : Math.max(0, w.capacity - reservation_count),
  };
}

// Reserve / cancel a seat — OPTIMISTIC. The page's Reserve/Cancel button flips instantly (and the
// "N joining" count nudges), so there's no success toast: the state change IS the confirmation
// (per repo convention for optimistic toggles). onError rolls the exact list back and the caller
// raises the error toast; onSettled reconciles with the server.
export function useToggleWebinarReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reserve }: { id: string; reserve: boolean }) =>
      reserve ? reserveWebinar(id) : cancelWebinarReservation(id),
    onMutate: async ({ id, reserve }) => {
      await qc.cancelQueries({ queryKey: UPCOMING_KEY });
      const prev = qc.getQueryData<Webinar[]>(UPCOMING_KEY);
      qc.setQueryData<Webinar[]>(UPCOMING_KEY, (old) =>
        (old ?? []).map((w) => (w.id === id ? applyReservation(w, reserve) : w)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(UPCOMING_KEY, ctx.prev);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}

// Reserve a seat from the "Reserve your seat" modal — a DELIBERATE confirm (not an instant toggle),
// so the caller DOES raise a success toast and closes the dialog. Not optimistic: a capacity check
// can legitimately reject it (webinar full), and the modal should reflect the true outcome.
export function useReserveWebinar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reserveWebinar(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}

export function useCreateWebinar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createWebinar,
    onSuccess: () => void qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}

export function useUpdateWebinar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWebinarInput }) => updateWebinar(id, input),
    // Rescheduling can move a webinar between the two tabs, so invalidate the whole prefix.
    onSuccess: () => void qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}

export function useDeleteWebinar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWebinar(id),
    // A deleted webinar could have been sitting in either tab — invalidate both rather than assume.
    onSuccess: () => void qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}
